/**
 * Customer name rules for the restaurant admin (task ordit1/tasks/tasks#54).
 *
 * The restaurant only sees the full customer name and the company when it handles
 * the delivery itself (restaurant managed delivery). For Ordit deliveries and
 * personal pickups the name is shortened to "First L." and the company is hidden.
 */

/**
 * "Alison Holmes" -> "Alison H.", "Mary Jane Watson" -> "Mary W.", "Madonna" -> "Madonna".
 * Mirrors OrderPayload::shortName() in the backend.
 *
 * @param {string|null|undefined} name
 * @returns {string|null|undefined}
 */
export function shortName(name) {
    if (name === null || name === undefined) return name
    const words = String(name).trim().split(/\s+/).filter(Boolean)
    if (words.length < 2) return words[0] || ''
    return `${words[0]} ${words[words.length - 1].charAt(0)}.`
}

/**
 * The delivery is done by the restaurant itself (no delivery job, or a
 * restaurant-managed one). Same rule as the `deliveryByYou` computed of the
 * restaurant admin components.
 */
export function isDeliveryByRestaurant(order) {
    return !(
        order?.deliveryJob &&
        order?.deliveryJob.primaryDeliveryMethod !== 'restaurant_managed'
    )
}

/**
 * Whether the restaurant may see the full customer name and the company name:
 * only for restaurant managed deliveries that are not personal pickups.
 */
export function showsFullCustomerName(order) {
    return isDeliveryByRestaurant(order) && !order?.personalPickup
}

/**
 * The customer name to display for the given order.
 *
 * The full name is shown as stored (`deliveryName` by default). The shortened name is
 * built from the user profile's first/last name when the order has one (so it is
 * "First L." even when `deliveryName` was stored in the Hungarian "Last First" order),
 * otherwise from the given full name.
 *
 * @param {object} order
 * @param {string|null|undefined} fullName the full name (defaults to order.deliveryName)
 */
export function customerName(order, fullName = order?.deliveryName) {
    if (showsFullCustomerName(order)) return fullName

    const profile = order?.userProfile
    if (
        profile &&
        typeof profile === 'object' &&
        (profile.firstName || profile.lastName)
    ) {
        return shortName(`${profile.firstName || ''} ${profile.lastName || ''}`)
    }

    return shortName(fullName)
}
